import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Alert, AlertDescription } from './ui/alert';
import { Loader2, Copy, CheckCircle, Mail, Link as LinkIcon, UserPlus } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function InvitePatient({ open, onClose, onInviteSent }) {
  const [step, setStep] = useState('form'); // 'form' | 'success'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    patient_email: '',
    patient_name: '',
    message: ''
  });
  const [invitation, setInvitation] = useState(null);
  const [codeCopied, setCodeCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/invitations/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          method: 'both'
        })
      });

      const data = await response.json();

      if (response.ok) {
        setInvitation(data);
        setStep('success');
        if (onInviteSent) onInviteSent();
      } else {
        setError(data.detail || 'Failed to send invitation');
      }
    } catch (err) {
      setError('Failed to send invitation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'code') {
        setCodeCopied(true);
        setTimeout(() => setCodeCopied(false), 2000);
      } else {
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
      }
      toast.success(`${type === 'code' ? 'Code' : 'Link'} copied to clipboard!`);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const handleClose = () => {
    // Reset state
    setStep('form');
    setFormData({ patient_email: '', patient_name: '', message: '' });
    setInvitation(null);
    setError('');
    setCodeCopied(false);
    setLinkCopied(false);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        {step === 'form' ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-orange-500" />
                Invite a Patient
              </DialogTitle>
              <DialogDescription>
                Send an invitation to connect a patient to your care team.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="patient_name">Patient Name</Label>
                <Input
                  id="patient_name"
                  data-testid="invite-patient-name"
                  value={formData.patient_name}
                  onChange={(e) => setFormData({ ...formData, patient_name: e.target.value })}
                  placeholder="John Doe"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="patient_email">Patient Email</Label>
                <Input
                  id="patient_email"
                  data-testid="invite-patient-email"
                  type="email"
                  value={formData.patient_email}
                  onChange={(e) => setFormData({ ...formData, patient_email: e.target.value })}
                  placeholder="patient@example.com"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">Personal Message (Optional)</Label>
                <Textarea
                  id="message"
                  data-testid="invite-message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Welcome to PlateMind! I look forward to helping you..."
                  rows={3}
                />
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <DialogFooter className="gap-2 sm:gap-0">
                <Button type="button" variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  data-testid="send-invite-btn"
                  className="bg-orange-500 hover:bg-orange-600"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send Invitation'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-green-600">
                <CheckCircle className="w-5 h-5" />
                Invitation Sent!
              </DialogTitle>
              <DialogDescription>
                Share the code or link with {invitation?.patient_name}.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 mt-4">
              {/* Invitation Code */}
              <div className="p-4 bg-orange-50 rounded-lg">
                <Label className="text-sm text-gray-500 mb-2 block">Invitation Code</Label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-2xl font-mono font-bold tracking-widest text-center py-2 bg-white rounded border">
                    {invitation?.invite_code}
                  </code>
                  <Button
                    variant="outline"
                    size="icon"
                    data-testid="copy-code-btn"
                    onClick={() => copyToClipboard(invitation?.invite_code, 'code')}
                  >
                    {codeCopied ? (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>

              {/* Invitation Link */}
              <div className="p-4 bg-blue-50 rounded-lg">
                <Label className="text-sm text-gray-500 mb-2 block">Or share this link</Label>
                <div className="flex items-center gap-2">
                  <Input
                    value={invitation?.invite_link}
                    readOnly
                    className="flex-1 text-sm bg-white"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    data-testid="copy-link-btn"
                    onClick={() => copyToClipboard(invitation?.invite_link, 'link')}
                  >
                    {linkCopied ? (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    ) : (
                      <LinkIcon className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>

              {/* Email status */}
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Mail className="w-4 h-4" />
                {invitation?.email_sent ? (
                  <span className="text-green-600">Email sent to {invitation?.patient_email}</span>
                ) : (
                  <span>Email not sent (share the code or link manually)</span>
                )}
              </div>

              {/* Expiry notice */}
              <p className="text-xs text-gray-400 text-center">
                This invitation expires in 7 days.
              </p>
            </div>

            <DialogFooter className="mt-4">
              <Button 
                onClick={handleClose}
                className="w-full bg-orange-500 hover:bg-orange-600"
              >
                Done
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
